<template>
  <BaseDialog max-width="800" persistent :value="true">
    <template #modal>
      <BaseCosmoCard>
        <template #title>
          <div class="modal_header">
            <BaseTitle v-text="modalTitle" />
            <BaseButtonIcon
              class="close_icon"
              icon="$mdiClose"
              :disabled="isSubmiting"
              @click="reset()"
            />
          </div>
        </template>
        <div class="modal_content">
          <base-text-field
            v-model="form.name"
            :label="`${$t('utils.title')}*`"
            :has-error="!!nameErrors"
            :error-messages="nameErrors"
          />
          <base-text-field
            v-model="form.external_id"
            :label="`${$t('utils.externalId')}*`"
            :has-error="!!externalIdErrors"
            :error-messages="externalIdErrors"
          />
          <div class="fileds_container">
            <!-- WORKFLOW FIELDS -->
            <div class="section">
              <div class="section_title" v-text="$t('utils.fiels_available')" />
              <div class="section_header">
                <v-text-field
                  v-model="wfQuestions.name"
                  class="search_field"
                  outlined
                  :placeholder="`${$t('utils.search')}...`"
                  prepend-inner-icon="$mdiMagnify"
                  dense
                  hide-details
                  @input="filterWfQuestions()"
                />
              </div>
              <div class="section_body">
                <button
                  v-for="(question, i) in formatedQuestions"
                  :key="`q-${i}`"
                  class="question_item"
                  @click.stop="selectQuestion(question)"
                >
                  <div class="info_section">
                    <div class="question_label" v-text="question.label" />
                    <div class="question_slug" v-text="question.slug" />
                  </div>
                  <div class="action">
                    <BaseButtonIcon
                      color="primary"
                      icon="$mdiArrowRight"
                      @click.stop="selectQuestion(question)"
                    />
                  </div>
                </button>
                <template v-if="wfQuestions.loading">
                  <div
                    v-for="value in 5"
                    :key="value"
                    class="folder_item_skeleton"
                  >
                    <v-skeleton-loader class="skeleton" type="text" />
                  </div>
                </template>
                <div v-else v-intersect="loadMoreWorkflowQuestions" />
              </div>
            </div>
            <!-- FORM FIELDS -->
            <div class="section">
              <div
                class="section_title"
                v-text="$t('controls.fields_to_control')"
              />
              <div class="section_header">
                <v-text-field
                  v-model="search_str"
                  class="search_field"
                  outlined
                  :placeholder="`${$t('utils.search')}...`"
                  prepend-inner-icon="$mdiMagnify"
                  dense
                  hide-details
                />
              </div>
              <div
                class="section_body"
                :class="{
                  error_section: questionTemplatesErrors.length,
                }"
              >
                <div
                  v-if="questionTemplatesErrors.length"
                  class="error_message"
                  v-text="questionTemplatesErrors"
                />
                <button
                  v-for="(question, i) in filteredSelectedQuestions"
                  :key="`q-${i}`"
                  class="question_item"
                  @click.stop="unSelectQuestion(question)"
                >
                  <div class="info_section">
                    <div class="question_label" v-text="question.label" />
                    <div class="question_slug" v-text="question.slug" />
                  </div>
                  <div class="action">
                    <BaseButtonIcon
                      color="secondary"
                      icon="$mdiClose"
                      @click.stop="unSelectQuestion(question)"
                    />
                  </div>
                </button>
              </div>
            </div>
          </div>
          <BaseMarkdownEditor
            v-model="form.description"
            :label="$t('organization.formDescription')"
          />
        </div>
        <div class="modal_footer">
          <BaseButton
            :disabled="isSubmiting"
            color="grey"
            type="secondary"
            @click="reset()"
          >
            {{ $t("utils.cancel") }}
          </BaseButton>
          <BaseButton
            :disabled="isSubmiting"
            :loading="isSubmiting"
            color="primary"
            type="primary"
            @click="submit"
          >
            {{ control ? $t("utils.edit") : $t("utils.create") }}
          </BaseButton>
        </div>
      </BaseCosmoCard>
    </template>
  </BaseDialog>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required, maxLength } from "vuelidate/lib/validators";
import { getWorkflowQuestions } from "../Services/workflow.service";
import { fetchQuestionTemplates } from "@/core/Services/questionTemplate.service";
import organizationApi from "@/core/Services/organization.api";

export default {
  name: "ControlCreateAndEditDialog",
  mixins: [validationMixin],
  props: {
    organizationId: {
      type: [Number, String],
      required: true,
    },
    control: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      form: {
        name: "",
        description: "",
        external_id: "",
        questions: [],
      },
      search_str: "",
      isSubmiting: false,
      wfQuestions: {
        page: 1,
        pageSize: 10,
        name: undefined,
        items: [],
        loading: true,
      },
    };
  },
  validations() {
    let validations = {};
    validations.form = {
      name: {
        required,
        maxLength: maxLength(255),
      },
      external_id: {
        required,
        maxLength: maxLength(255),
      },
      questions: {
        required,
      },
    };
    return validations;
  },

  computed: {
    formatedQuestions() {
      return this.wfQuestions.items
        .map((q) => {
          return { ...q, label: `${q.name}` };
        })
        .filter((q) => !this.form.questions.some((fq) => fq.id == q.id));
    },
    filteredSelectedQuestions() {
      return this.form.questions
        .map((q) => ({
          ...q,
          label: q.status ? `${q.name} ( ${q.status} )` : q.name,
        }))
        .filter((q) =>
          !this.search_str
            ? q
            : q.label.toLowerCase().includes(this.search_str.toLowerCase())
        );
    },
    modalTitle() {
      return this.control
        ? this.$t("controls.edit_control")
        : this.$t("controls.create_control");
    },
    nameErrors() {
      if (!this.$v.form.name.$dirty) {
        return "";
      }
      if (!this.$v.form.name.required) {
        return this.$t("folder.records.createAndEditModal.nameRequired");
      }
      if (!this.$v.form.name.maxLength) {
        return this.$t("folder.records.createAndEditModal.nameMaxLength");
      }
      return "";
    },
    externalIdErrors() {
      if (!this.$v.form.external_id.$dirty) {
        return "";
      }
      if (!this.$v.form.external_id.required) {
        return this.$t("cosmoform.dialog.externalIdRequired");
      }
      return "";
    },
    questionTemplatesErrors() {
      if (!this.$v.form.questions.$dirty) {
        return "";
      }
      if (!this.$v.form.questions.required) {
        return "Question template required";
      }
      return "";
    },
  },
  created() {
    this.fetchWorkflowQuestion();

    if (this.control) {
      this.wfQuestions = {
        ...this.wfQuestions,
        ...this.control.questionTemplates,
      };

      return (this.form = {
        name: this.control.name,
        description: this.control.description,
        external_id: this.control.externalId,
        questions: [...this.control.questionTemplates],
      });
    }
  },
  methods: {
    reset() {
      this.form = {
        type: null,
        name: "",
        description: "",
        files: [],
      };
      this.$v.form.$reset();
      this.$emit("close");
    },
    selectQuestion(question) {
      this.form.questions.unshift(question);
    },
    unSelectQuestion(question) {
      this.form.questions = this.form.questions.filter(
        (q) => q.id != question.id
      );
    },
    async submit() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) {
        return;
      }
      try {
        this.isSubmiting = true;
        const payload = {
          name: this.form.name,
          description: this.form.description,
          external_id: this.form.external_id,
          question_templates: this.form.questions.map((q) => q.id),
        };
        let message = this.$t("controls.control_edited_successfully");
        if (this.control) {
          await organizationApi.patchOrganizationValidationSchema({
            organizationId: this.organizationId,
            validationSchemaId: this.control.id,
            payload,
          });
        } else {
          await organizationApi.postOrganizationValidationSchema({
            organizationId: this.organizationId,
            payload,
          });
          message = this.$t("controls.control_created_successfully");
        }
        this.$emit("fetch-controls");
        this.$store.dispatch("snackbar/active", {
          message,
          type: "SUCCESS",
        });
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.isSubmiting = false;
      this.reset();
    },
    async fetchWorkflowQuestion() {
      this.wfQuestions.loading = true;
      try {
        this.wfQuestions.page = 1;
        const response = await fetchQuestionTemplates({
          organizationId: this.organizationId,
          page: this.wfQuestions.page,
          pageSize: this.wfQuestions.pageSize,
          name: this.wfQuestions.name,
        });
        this.wfQuestions = { ...this.wfQuestions, ...response };
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.wfQuestions.loading = false;
    },
    filterWfQuestions() {
      if (this.wfQuestions.searchTimeoutRef)
        clearTimeout(this.wfQuestions.searchTimeoutRef);
      this.wfQuestions.searchTimeoutRef = setTimeout(async () => {
        this.fetchWorkflowQuestion();
      }, 200);
    },
    async loadMoreWorkflowQuestions(_, __, isIntersecting) {
      if (!isIntersecting || this.wfQuestions.loading || !this.wfQuestions.next)
        return;
      this.wfQuestions.page = this.wfQuestions.next;
      this.wfQuestions.loading = true;
      try {
        const tmp = await getWorkflowQuestions({
          organizationId: this.$route.params.organizationId,
          workflowId: this.$route.params.workflowId,
          page: this.wfQuestions.page,
          name: this.wfQuestions.name,
          pageSize: this.wfQuestions.pageSize,
          std_only: true,
        });
        this.wfQuestions = {
          ...this.wfQuestions,
          ...tmp,
          items: [...this.wfQuestions.items, ...tmp.items],
        };
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.wfQuestions.loading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.modal_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
  padding-top: 8px;
  flex-wrap: wrap;
  .modal_title {
    font-weight: 600;
    font-size: 24px;
    line-height: 32px;
    color: #353542;
  }
  .close_icon {
    margin-left: auto;
  }
}
.modal_content {
  padding-top: 24px;
  position: relative;
  .sub-title {
    font-family: "Inter";
    font-weight: 600;
    font-size: 16px;
    line-height: 19px;
    color: #353542;
  }
  .input-fields {
    padding: 16px 16px 0 16px;
    margin-bottom: 24px;
    .input_label {
      color: #353542;
      font-family: "Inter";
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 18px;
      margin-bottom: 16px;
    }
  }
  .offer-nature-container {
    display: flex;
    gap: 24px;
    margin-top: 24px;
    .offer-item {
      flex: 1;
    }
  }
  .file-drag {
    width: 400px;
    height: 100px;
    background: lightgrey;
  }
  ::v-deep .v-menu__content {
    top: 44px !important;
  }

  ::v-deep .v-skeleton-loader__text {
    height: 40px;
  }

  ::v-deep .v-skeleton-loader__heading .v-skeleton-loader__bone {
    display: none;
  }
  .action-container {
    display: flex;
    align-items: center;
    margin-top: 32px;
  }
  .fileds_container {
    width: 100%;
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
    .section {
      flex: none;
      width: calc(50% - 10px);
      .section_title {
        font-size: 16px;
        line-height: 20px;
        margin-bottom: 10px;
        color: #353542;
      }
      .section_header {
        padding: 8px;
        background: #ecebf1;
        .search_field {
          background: #fff;
        }
      }
      .section_body {
        position: relative;
        border: 0.5px solid #ecebf1;
        padding: 12px;
        height: 312px;
        overflow: hidden;
        overflow-y: auto;
        &.error_section {
          border-color: red;
        }
        .question_item {
          padding: 6px 10px;
          align-items: center;
          display: flex;
          width: 100%;
          gap: 4px;
          border-radius: 5px;
          margin-bottom: 12px;
          cursor: pointer;
          text-align: start;
          background: #fff;
          transition: all 0.25s ease-in-out;
          &:hover {
            background: #f4f7ff;
          }
          .info_section {
            width: calc(100% - 40px);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            .question_label {
              color: #2536cc;
              font-size: 12px;
              font-weight: 600;
              line-height: 20px;
            }
            .question_slug {
              color: #707080;
              font-size: 12px;
              font-weight: 400;
              line-height: 16px;
              text-transform: uppercase;
            }
          }
          .action {
            width: 36px;
            height: 36px;
            flex: none;
          }
        }
        .error_message {
          text-align: center;
          color: red;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
        .folder_item_skeleton {
          width: 100%;
          height: 44px;
          margin-bottom: 12px;
          .skeleton {
            height: 100%;
            width: 100%;
            ::v-deep {
              .v-skeleton-loader__bone {
                height: 100%;
              }
            }
          }
        }
      }
    }
  }
  .validation_schemas_section {
    margin-top: 20px;
    .validation_schema_skeleton {
      height: 64px;
      width: 100%;
      margin-bottom: 24px;
      ::v-deep {
        .v-skeleton-loader__bone {
          height: 64px;
        }
      }
    }
    .validation_schemas {
      padding: 0 24px;
      margin-bottom: 16px;
      .schema_item {
        display: flex;
        gap: 8px;
        padding: 8px 0;
        .item_checkbox {
          width: 18px;
          height: 18px;
          cursor: pointer;
          margin-top: 2px;
        }
        .check_label {
          color: #333;
          font-family: Inter;
          font-size: 14px;
          line-height: 20px;
        }
        .quesiton_label {
          color: #666;
          font-family: Inter;
          font-size: 12px;
          line-height: 18px;
          strong {
            font-weight: bold;
          }
        }
      }
    }
  }
}
.modal_footer {
  display: flex;
  justify-content: flex-end;
  align-content: center;
  margin-top: 16px;
  padding: 8px 0;
  gap: 16px;
}
</style>
